import type { AuthProvider } from "react-admin";
import { ADMIN_LOGIN } from "../constants/paths";
import { translateApp } from "../i18n/translateApp";
import { API_BASE, REFRESH_KEY, SELF_KEY, TOKEN_KEY } from "./config";

export interface SelfClaims {
  userId: number;
  email?: string | null;
  phone?: string | null;
  roles: string[];
}

const decodeJwt = (token: string): Record<string, unknown> => {
  try {
    const payload = token.split(".")[1];
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch {
    return {};
  }
};

const claimsFromToken = (token: string, role?: string): SelfClaims => {
  const c = decodeJwt(token);
  const rolesClaim =
    (c["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] as
      | string
      | string[]
      | undefined) ?? c.role ?? role ?? [];
  return {
    userId: Number(c.sub ?? c.userId ?? 0),
    email: (c["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"] ??
      c.email) as string | undefined,
    phone: c.phone as string | undefined,
    roles: Array.isArray(rolesClaim) ? rolesClaim : [rolesClaim].filter(Boolean),
  };
};

export const authProvider: AuthProvider = {
  async login({ username, password }: { username: string; password: string }) {
    const isEmail = username.includes("@");
    const body = isEmail
      ? { email: username, password, login: username }
      : { phone: username, password, login: username };

    const res = await fetch(`${API_BASE}/auth/signIn?longToken=true`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error((err as { message?: string })?.message ?? translateApp("auth.loginError"));
    }

    const data = await res.json();
    if (!data?.auth) throw new Error(translateApp("auth.loginError"));

    const claims = claimsFromToken(data.auth, data.role);
    const allowed = ["Admin", "Manager", "Doctor", "ClinicAdmin"];
    if (!claims.roles.some((r) => allowed.includes(r)) && data.role !== "Admin") {
      throw new Error(translateApp("auth.noAdminAccess"));
    }

    localStorage.setItem(TOKEN_KEY, data.auth);
    localStorage.setItem(REFRESH_KEY, data.refresh ?? "");
    localStorage.setItem(SELF_KEY, JSON.stringify(claims));
    return Promise.resolve();
  },

  async logout() {
    const refresh = localStorage.getItem(REFRESH_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(SELF_KEY);
    if (refresh) {
      try {
        await fetch(`${API_BASE}/auth/signOut`, {
          method: "POST",
          headers: { Authorization: `Bearer ${refresh}` },
        });
      } catch {
        // ignore
      }
    }
    return Promise.resolve();
  },

  async checkAuth() {
    return localStorage.getItem(TOKEN_KEY) ? Promise.resolve() : Promise.reject();
  },

  async checkError(error: { status?: number; response?: { status?: number } }) {
    const status = error?.status ?? error?.response?.status;
    if (status === 403) return Promise.resolve();
    if (status === 401) {
      clearAuthStorage();
      return Promise.reject({ logoutUser: true, redirectTo: "/login" });
    }
    return Promise.resolve();
  },

  async getIdentity() {
    const raw = localStorage.getItem(SELF_KEY);
    if (!raw) return Promise.reject();
    const c = JSON.parse(raw) as SelfClaims;
    return Promise.resolve({
      id: c.userId,
      fullName: c.email ?? c.phone ?? `User #${c.userId}`,
    });
  },

  async getPermissions() {
    const raw = localStorage.getItem(SELF_KEY);
    if (!raw) return Promise.resolve([]);
    return Promise.resolve(JSON.parse(raw) as SelfClaims);
  },
};

export const clearAuthStorage = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(SELF_KEY);
};

export class SessionExpiredError extends Error {
  status = 401;
  logoutUser = true;
  constructor(message = "Session expired") {
    super(message);
    this.name = "SessionExpiredError";
  }
}

export const handleSessionExpired = () => {
  clearAuthStorage();
  const login = `${window.location.origin}${ADMIN_LOGIN}`;
  if (!window.location.pathname.startsWith(ADMIN_LOGIN)) {
    window.location.assign(login);
  }
};

let inFlight: Promise<string | null> | null = null;

export const tryRefresh = async (): Promise<string | null> => {
  if (inFlight) return inFlight;
  const refresh = localStorage.getItem(REFRESH_KEY);
  if (!refresh) return null;

  inFlight = (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: "POST",
        headers: { Authorization: `Bearer ${refresh}` },
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (!data?.auth) return null;
      const claims = claimsFromToken(data.auth, data.role);
      localStorage.setItem(TOKEN_KEY, data.auth);
      if (data.refresh) localStorage.setItem(REFRESH_KEY, data.refresh);
      localStorage.setItem(SELF_KEY, JSON.stringify(claims));
      return data.auth as string;
    } catch {
      return null;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
};
